/**
 * Modal de recomendaciones después de añadir al carrito
 * Actualiza dinámicamente con el producto recién añadido
 */

// Locale prefix for translated storefront URLs (e.g. '/en', '/de')
const _localePrefix = (function() {
  const lang = document.documentElement.lang || '';
  const shopLocale = window.Shopify && window.Shopify.locale;
  const locale = shopLocale || lang;
  // Default locale (es) doesn't need a prefix
  return (locale && locale !== 'es') ? '/' + locale : '';
})();

if (!customElements.get('cart-upsell-modal')) {
  customElements.define(
    'cart-upsell-modal',
    class CartUpsellModal extends HTMLElement {
      constructor() {
        super();

        // Botón de cerrar
        const closeButton = this.querySelector('#ModalClose-cart-upsell');
        if (closeButton) {
          closeButton.addEventListener('click', () => this.hide());
        }

        // Cerrar al hacer clic en el overlay
        this.addEventListener('click', (e) => {
          if (e.target === this) {
            this.hide();
          }
        });

        // Cerrar con Escape
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.hasAttribute('open')) {
            this.hide();
          }
        });
      }

      /**
       * Actualiza el producto mostrado en el modal
       */
      updateAddedProduct(item, cartQuantity = item.quantity) {
        const productContainer = this.querySelector('#cart-upsell-added-product');
        if (!productContainer) return;

        productContainer.querySelectorAll('[data-express-recommendation-badge]').forEach(node => node.remove());
        if (item.url) window.expressUI?.attachCardBadge(productContainer, item.url, {variantId: item.variant_id || item.id, quantity: cartQuantity});

        const productImage = productContainer.querySelector('.cart-upsell-added__product-image');
        const productTitle = productContainer.querySelector('.cart-upsell-added__product-title');
        const productInfo = productContainer.querySelector('.cart-upsell-added__product-info');

        if (productImage && item.image) {
          productImage.src = item.image;
          productImage.alt = item.product_title;
        }

        if (productTitle) {
          productTitle.textContent = item.product_title;
        }

        // Actualizar o añadir variante
        let variantElement = productInfo.querySelector('.cart-upsell-added__product-variant');
        if (item.variant_title && item.variant_title !== 'Default Title' && item.variant_title !== item.product_title) {
          if (!variantElement) {
            variantElement = document.createElement('div');
            variantElement.className = 'cart-upsell-added__product-variant';
            productTitle.after(variantElement);
          }
          variantElement.textContent = item.variant_title;
        } else if (variantElement) {
          variantElement.remove();
        }
      }

      /**
       * Actualiza las recomendaciones del modal
       */
      async updateRecommendations(productHandle) {
        const recommendationsContainer = this.querySelector('#cart-upsell-recommendations-grid');
        if (!recommendationsContainer) return;

        try {
          // Obtener metafields del producto usando template alternativo
          const response = await fetch(`${_localePrefix}/products/${productHandle}?view=recommendations`);
          if (!response.ok) {
            console.log('No recommendations template, keeping Liquid defaults');
            return;
          }

          const data = await response.json();

          if (!data.recommendedHandles || data.recommendedHandles.length === 0) {
            console.log('No recommendations found for this product, keeping Liquid defaults');
            return;
          }

          // Obtener productos del carrito para filtrarlos
          const cartResponse = await fetch(`${_localePrefix}/cart.js`);
          const cartData = await cartResponse.json();
          const cartProductIds = cartData.items.map(item => item.product_id);

          // Renderizar recomendaciones
          await this.renderRecommendations(data.recommendedHandles, cartProductIds, recommendationsContainer);
        } catch (error) {
          console.error('Error loading recommendations, keeping Liquid defaults:', error);
          // No hacer nada, mantener las recomendaciones de Liquid
        }
      }

      /**
       * Renderiza las recomendaciones en el modal
       */
      async renderRecommendations(handles, cartProductIds, container) {
        // Solo actualizar si hay recomendaciones
        if (!handles || handles.length === 0) {
          return; // Mantener las recomendaciones por defecto de Liquid
        }

        const newElements = [];
        let count = 0;
        const maxRecommendations = 10;

        // Cargar productos del metafield (Priority 1)
        for (const handle of handles) {
          if (count >= maxRecommendations) break;

          try {
            const response = await fetch(`${_localePrefix}/products/${handle}.js`);
            const product = await response.json();

            // Saltar si ya está en el carrito
            if (cartProductIds.includes(product.id)) continue;

            // Saltar si no está disponible o precio <= 0
            if (!product.available || product.price <= 0) continue;

            // Crear elemento de recomendación con Priority 1
            const recElement = this.createRecommendationElement(product);
            newElements.push(recElement);
            count++;
          } catch (error) {
            console.error(`Error loading product ${handle}:`, error);
          }
        }

        // Si hay AL MENOS 1 producto del metafield, reemplazar y completar con fallbacks del DOM si es necesario
        if (newElements.length > 0) {
          // Si tenemos menos de 10, añadir productos del DOM con priority 2 y 3 para completar
          if (count < maxRecommendations) {
            const existingRecs = Array.from(container.querySelectorAll('.cart-upsell-recommendation[data-priority="2"], .cart-upsell-recommendation[data-priority="3"]'));

            // Filtrar los que ya están en el carrito
            for (const rec of existingRecs) {
              if (count >= maxRecommendations) break;

              const productId = parseInt(rec.dataset.productId);
              if (!cartProductIds.includes(productId)) {
                newElements.push(rec.cloneNode(true));
                count++;
              }
            }
          }

          // Reemplazar contenido
          container.innerHTML = '';
          newElements.forEach(el => container.appendChild(el));

          // Barajar respetando prioridades después de actualizar
          this.shuffleRecommendationsByPriority();
        }
        // Si no hay ningún producto del metafield, mantener las de Liquid que incluyen fallbacks
      }

      /**
       * Crea el elemento HTML de una recomendación
       */
      createRecommendationElement(product) {
        const a = document.createElement('a');
        a.href = product.url;
        a.className = 'cart-upsell-recommendation';
        a.dataset.productId = product.id;
        a.dataset.priority = '1'; // Metafield products are always priority 1

        const priceHTML = this.formatPrice(product);

        a.innerHTML = `
          <img
            src="${product.featured_image}"
            alt="${product.title}"
            class="cart-upsell-recommendation__image"
            loading="lazy"
          >
          <div class="cart-upsell-recommendation__info">
            <div class="cart-upsell-recommendation__title">${product.title}</div>
            <div class="cart-upsell-recommendation__price">${priceHTML}</div>
          </div>
          <svg class="cart-upsell-recommendation__arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M7 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;

        a.dataset.expressRecommendationUrl = product.url;
        window.expressUI?.attachCardBadge(a, product.url);
        return a;
      }

      /**
       * Formatea un precio en centavos usando el formato de dinero de Shopify
       */
      formatMoney(cents) {
        if (typeof cents === 'string') {
          cents = cents.replace('.', '');
        }

        // Convertir a número
        cents = parseInt(cents, 10);

        if (isNaN(cents)) {
          cents = 0;
        }

        // Formato: 12345 centavos = 123,45€
        // Separador de miles: punto (.)
        // Separador de decimales: coma (,)
        const euros = Math.floor(cents / 100);
        const centimos = Math.abs(cents % 100);

        // Formatear euros con separador de miles
        const eurosFormatted = euros.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

        // Formatear céntimos con 2 dígitos
        const centimosFormatted = centimos.toString().padStart(2, '0');

        // Retornar en formato: 123.456,78€
        return `${eurosFormatted},${centimosFormatted}€`;
      }

      /**
       * Formatea el precio del producto (misma lógica que /cart)
       */
      formatPrice(product) {
        const price = product.price; // Ya viene en centavos
        const comparePrice = product.compare_at_price || null;
        const hasVariantPrices = product.price_min !== product.price_max;

        // Obtener settings de promo (si existen en el DOM)
        const promoEnabled = document.body.dataset.promoEnabled === 'true';
        const promoDiscount = parseFloat(document.body.dataset.promoDiscount || '1');

        // Idioma base: la tienda sirve pt-PT y las claves de abajo son de dos letras.
        const locale = (document.documentElement.lang || 'es').split('-')[0];
        const fromText = {
          'es': 'Desde',
          'en': 'From',
          'de': 'Ab',
          'fr': 'À partir de',
          'it': 'Da',
          'pt': 'Desde'
        }[locale] || 'From';

        let priceText = '';

        // Añadir "Desde" si hay precios variables
        if (hasVariantPrices) {
          priceText += `<span class="price-from">${fromText} </span>`;
        }

        if (promoEnabled && promoDiscount < 1) {
          // CON PROMOCIÓN: mostrar precio con descuento
          const discountedPrice = Math.round(price * promoDiscount);
          priceText += `<span class="price-current">${this.formatMoney(discountedPrice)}</span> `;

          // Precio original tachado
          if (comparePrice && comparePrice > price) {
            priceText += `<span class="price-original">${this.formatMoney(comparePrice)}</span>`;
          } else {
            priceText += `<span class="price-original">${this.formatMoney(price)}</span>`;
          }
        } else {
          // SIN PROMOCIÓN: mostrar precio normal
          priceText += `<span class="price-current">${this.formatMoney(price)}</span>`;

          // Si hay compare_at_price mayor, mostrarlo tachado
          if (comparePrice && comparePrice > price) {
            priceText += ` <span class="price-original">${this.formatMoney(comparePrice)}</span>`;
          }
        }

        return priceText;
      }

      /**
       * Baraja las recomendaciones respetando prioridades
       */
      shuffleRecommendationsByPriority() {
        const container = this.querySelector('#cart-upsell-recommendations-grid');
        if (!container) return;

        const items = Array.from(container.querySelectorAll('.cart-upsell-recommendation'));
        if (items.length === 0) return;

        // Agrupar por prioridad
        const groups = {};
        items.forEach(item => {
          const priority = item.getAttribute('data-priority') || '999';
          if (!groups[priority]) groups[priority] = [];
          groups[priority].push(item);
        });

        // Fisher-Yates shuffle para cada grupo
        const shuffle = (array) => {
          const arr = [...array];
          for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
          }
          return arr;
        };

        // Barajar cada grupo y concatenar en orden de prioridad
        const priorities = Object.keys(groups).sort((a, b) => a - b);
        const shuffledItems = [];

        priorities.forEach(priority => {
          const shuffledGroup = shuffle(groups[priority]);
          shuffledItems.push(...shuffledGroup);
        });

        // Reemplazar en el contenedor
        container.innerHTML = '';
        shuffledItems.slice(0, 10).forEach(item => {
          container.appendChild(item);
        });
      }

      /**
       * Filtra productos del carrito de las recomendaciones actuales en el DOM
       */
      async filterCartProductsFromRecommendations() {
        const container = this.querySelector('#cart-upsell-recommendations-grid');
        if (!container) return;

        try {
          // Obtener carrito actual
          const cartResponse = await fetch(`${_localePrefix}/cart.js`);
          const cartData = await cartResponse.json();
          const cartProductIds = cartData.items.map(item => item.product_id);

          // Filtrar elementos del DOM
          const recommendations = container.querySelectorAll('.cart-upsell-recommendation');
          recommendations.forEach(rec => {
            const productId = parseInt(rec.dataset.productId);
            if (cartProductIds.includes(productId)) {
              rec.remove(); // Quitar si ya está en el carrito
            }
          });
        } catch (error) {
          console.error('Error filtering cart products:', error);
        }
      }

      /**
       * Espera a que el aviso de cookies deje de tapar la ventana (max 25s).
       */
      esperarAvisoCookies() {
        const visible = () => {
          const b = document.querySelector('#shopify-pc__banner');
          if (!b) return false;
          const s = getComputedStyle(b);
          return s.display !== 'none' && s.visibility !== 'hidden' && b.getBoundingClientRect().height > 20;
        };
        if (!visible()) return Promise.resolve();
        return new Promise((resolve) => {
          let hecho = false;
          const fin = () => { if (hecho) return; hecho = true; obs.disconnect(); clearInterval(t); resolve(); };
          const obs = new MutationObserver(() => { if (!visible()) fin(); });
          obs.observe(document.body, { subtree: true, attributes: true, childList: true });
          const t = setInterval(() => { if (!visible()) fin(); }, 300);
          setTimeout(fin, 25000);
        });
      }

      /**
       * Muestra el modal
       */
      async show() {
        // FIX 2026-09-15: el aviso de cookies de Shopify (z-index 2.000.000) tapaba
        // por completo esta ventana en movil, dejando el boton de pagar invisible.
        // Si el aviso esta encima, esperamos a que el cliente lo despache.
        await this.esperarAvisoCookies();
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');

        // PRIMERO: Filtrar productos que ya están en el carrito
        await this.filterCartProductsFromRecommendations();

        // LUEGO: Barajar recomendaciones respetando prioridades
        this.shuffleRecommendationsByPriority();
        this.querySelectorAll('[data-express-recommendation-url]').forEach(card => {
          window.expressUI?.attachCardBadge(card, card.dataset.expressRecommendationUrl);
        });

        // Cerrar el cart-drawer si está abierto
        setTimeout(() => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.classList.contains('active')) {
            cartDrawer.close();
          }
        }, 100);
      }

      /**
       * Cierra el modal
       */
      hide() {
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }
    }
  );
}

// Cuando se añade al carrito desde product-form, actualizar y mostrar modal
function registerCartListener() {
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event.source === 'product-form') {
        const modal = document.getElementById('cart-upsell-modal');
        if (!modal) return;

        try {
          // Obtener el carrito actualizado
          const cartResponse = await fetch(`${_localePrefix}/cart.js`);
          const cartData = await cartResponse.json();

          // Buscar el item que se acaba de añadir
          const variantId = parseInt(event.productVariantId);
          const addedItem = cartData.items.find(item => item.variant_id === variantId);

          if (addedItem) {
            // Actualizar el producto mostrado (imagen, título, etc)
            const cartQuantity = cartData.items.filter(item => item.variant_id === variantId).reduce((sum, item) => sum + item.quantity, 0);
            modal.updateAddedProduct(addedItem, cartQuantity);

            // Obtener las recomendaciones del producto añadido directamente
            // Usamos la página del producto para obtener las recomendaciones renderizadas
            const productHandle = addedItem.handle;
            if (productHandle) {
              const response = await fetch(`${_localePrefix}/products/${productHandle}?sections=cart-upsell`);

              if (response.ok) {
                const data = await response.json();
                const sectionHtml = data['cart-upsell'];

                if (sectionHtml) {
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = sectionHtml;

                  // Solo actualizar la grid de recomendaciones
                  const newRecommendationsGrid = tempDiv.querySelector('#cart-upsell-recommendations-grid');
                  const currentRecommendationsGrid = modal.querySelector('#cart-upsell-recommendations-grid');

                  if (newRecommendationsGrid && currentRecommendationsGrid) {
                    currentRecommendationsGrid.innerHTML = newRecommendationsGrid.innerHTML;
                  }
                }
              }
            }
          }

          // Mostrar modal DESPUÉS de actualizar
          await modal.show();
        } catch (error) {
          console.error('Error updating modal:', error);
          // Mostrar modal incluso si hay error
          modal.show();
        }
      }
    });
    return true;
  } else {
    setTimeout(registerCartListener, 100);
    return false;
  }
}

// Intentar registrar inmediatamente y también después de DOMContentLoaded
registerCartListener();
document.addEventListener('DOMContentLoaded', registerCartListener);
